import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "../../convex/_generated/api";
import { useMutation } from "@tanstack/react-query";
import { useConvexMutation } from "@convex-dev/react-query";
import { Id } from "../../convex/_generated/dataModel";
import { getTasks } from "../functions/getTasks";

export const Route = createFileRoute("/tasks")({
  component: Tasks,
});

function Tasks() {
  const { data: tasks, error } = useQuery(convexQuery(api.tasks.get, {}));
  const queryClient = useQueryClient();

  const { mutate: createTask } = useMutation({
    mutationFn: useConvexMutation(api.tasks.create).withOptimisticUpdate(
      (localStore, args) => {
        const currentTasks = getTasks(localStore, queryClient);
        const id = crypto.randomUUID() as Id<"tasks">;
        localStore.setQuery(api.tasks.get, {}, [
          ...(currentTasks || []),
          {
            text: args.text,
            isCompleted: false,
            localId: id,
            _id: id,
            _creationTime: Date.now(),
          },
        ]);
      }
    ),
  });

  const { mutate: deleteTask } = useMutation({
    mutationFn: useConvexMutation(api.tasks.remove).withOptimisticUpdate(
      (localStore, args) => {
        const currentTasks = getTasks(localStore, queryClient);

        const newTasks = currentTasks?.filter((task) => task._id !== args.id);
        localStore.setQuery(api.tasks.get, {}, newTasks);
      }
    ),
  });

  const { mutate: updateTask } = useMutation({
    mutationFn: useConvexMutation(api.tasks.update).withOptimisticUpdate(
      (localStore, args) => {
        const currentTasks = getTasks(localStore, queryClient);
        const newTasks = currentTasks?.map((task) =>
          task._id === args.id
            ? { ...task, isCompleted: args.isCompleted }
            : task
        );
        localStore.setQuery(api.tasks.get, {}, newTasks);
      }
    ),
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    const form = e.target as HTMLFormElement;
    e.preventDefault();
    const formData = new FormData(form);
    const text = formData.get("text") as string;
    createTask({ text, localId: crypto.randomUUID() });
    form.reset();
  }

  if (error) return <div>Error: {error.message}</div>;

  return (
    <div className="p-2">
      <h1>Tasks</h1>
      <form className="flex gap-2" onSubmit={handleSubmit}>
        <input
          type="text"
          name="text"
          className="border p-1 rounded"
          placeholder="Enter a new task"
        />
        <button
          type="submit"
          className="bg-blue-500 text-white p-1 px-4 rounded hover:bg-blue-600"
        >
          Add
        </button>
      </form>
      <ul className="mt-4">
        {tasks?.map((task) => (
          <li key={task._id} className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={task.isCompleted}
              className="cursor-pointer"
              onChange={(e) =>
                updateTask({ id: task._id, isCompleted: e.target.checked })
              }
            />
            <span>{task.text}</span>
            <button
              className="text-red-500 hover:text-red-600 cursor-pointer"
              onClick={() => deleteTask({ id: task._id })}
            >
              &times;
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
